import React from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { ProgressSpinner } from 'primereact/progressspinner';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import APIFDFSectionSkeleton from '../APIFDFSectionSkeleton/APIFDFSectionSkeleton';
import useAPIFDFSection from './useAPIFDFSection.logic';
import { APIFDFSectionProps } from './APIFDFSection.types';
import './APIFDFSection.styles.scss';

const APIFDFSection: React.FC<APIFDFSectionProps> = ({ initialData }) => {
  const {
    importData,
    exportData,
    isLoading,
    isImportLoading,
    isExportLoading,
    error,
    activeTabIndex,
    setActiveTabIndex,
    importFilters,
    exportFilters,
    importPagination,
    exportPagination,
    onImportPageChange,
    onExportPageChange,
    onImportFilterChange,
    onExportFilterChange,
  } = useAPIFDFSection({ initialData });

  // Format currency values
  const formatCurrency = (value: string | null): string => {
    if (!value) return '-';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  // Format quantity values
  const formatQuantity = (value: string | null): string => {
    if (!value) return '-';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  // Format date values
  const formatDate = (value: string | null): string => {
    if (!value) return '-';
    try {
      const date = new Date(value);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return value;
    }
  };

  // Body templates for formatted columns
  const currencyBodyTemplate = (rowData: Record<string, unknown>, field: string) => {
    return formatCurrency(rowData[field] as string | null);
  };

  const quantityBodyTemplate = (rowData: Record<string, unknown>, field: string) => {
    return formatQuantity(rowData[field] as string | null);
  };

  const dateBodyTemplate = (rowData: Record<string, unknown>, field: string) => {
    return formatDate(rowData[field] as string | null);
  };

  const textBodyTemplate = (rowData: Record<string, unknown>, field: string) => {
    return (rowData[field] as string) || '-';
  };

  // Text filter template with disabled state during loading
  const createTextFilterTemplate = (isDisabled: boolean) => {
    return (options: ColumnFilterElementTemplateOptions) => (
      <InputText
        value={options.value ?? ''}
        onChange={e => options.filterApplyCallback(e.target.value)}
        placeholder="Search"
        className="p-column-filter"
        disabled={isDisabled}
        style={{ width: '100%', opacity: isDisabled ? 0.6 : 1 }}
      />
    );
  };

  // Date range filter template for date columns with disabled state
  const createDateRangeFilterTemplate = (isDisabled: boolean) => {
    return (options: ColumnFilterElementTemplateOptions) => {
      const [from, to] = options.value ?? [null, null];

      return (
        <div
          className="flex gap-1"
          style={{ display: 'flex', gap: '8px', flexDirection: 'row', width: '100%' }}
        >
          <Calendar
            value={from}
            onChange={e => options.filterApplyCallback([e.value, to])}
            placeholder="From"
            dateFormat="mm/dd/yy"
            showIcon
            className="p-column-filter"
            style={{ width: '9rem', opacity: isDisabled ? 0.6 : 1 }}
            disabled={isDisabled}
          />
          <Calendar
            value={to}
            onChange={e => options.filterApplyCallback([from, e.value])}
            placeholder="To"
            dateFormat="mm/dd/yy"
            showIcon
            className="p-column-filter"
            style={{ width: '9rem', opacity: isDisabled ? 0.6 : 1 }}
            disabled={isDisabled}
          />
        </div>
      );
    };
  };

  // Create filter templates for import tab (disabled when loading)
  const importTextFilterTemplate = createTextFilterTemplate(isImportLoading);
  const importDateFilterTemplate = createDateRangeFilterTemplate(isImportLoading);

  // Create filter templates for export tab (disabled when loading)
  const exportTextFilterTemplate = createTextFilterTemplate(isExportLoading);
  const exportDateFilterTemplate = createDateRangeFilterTemplate(isExportLoading);

  // Loading overlay component for lazy loading
  const LoadingOverlay = () => (
    <div className="apifdf-loading-overlay">
      <ProgressSpinner
        style={{ width: '50px', height: '50px' }}
        strokeWidth="4"
        animationDuration=".5s"
      />
    </div>
  );

  if (isLoading) {
    return <APIFDFSectionSkeleton />;
  }

  if (error) {
    return (
      <div className="apifdf-section">
        <h4 className="apifdf-section-title">APIFDF Import & Export Data</h4>
        <div className="apifdf-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="apifdf-section">
      <h4 className="apifdf-section-title">APIFDF Import & Export Data</h4>

      <TabView
        activeIndex={activeTabIndex}
        onTabChange={e => setActiveTabIndex(e.index)}
        className="apifdf-tabview"
      >
        <TabPanel
          header={
            <div
              onClick={() => setActiveTabIndex(0)}
              className={`apifdf-tab-header ${activeTabIndex === 0 ? 'apifdf-tab-selected' : 'apifdf-tab-unselected'}`}
            >
              Import
            </div>
          }
        >
          <div className="apifdf-datatable-wrapper">
            {isImportLoading && <LoadingOverlay />}
            <DataTable
              value={importData}
              lazy
              paginator
              first={importPagination.first}
              rows={importPagination.pageSize}
              totalRecords={importPagination.totalRecords}
              onPage={onImportPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              emptyMessage="No import records found"
              scrollable
              scrollHeight="100rem"
              stripedRows
              filters={importFilters}
              onFilter={onImportFilterChange}
              filterDisplay="row"
              loading={isImportLoading}
              globalFilterFields={[
                'importer_name',
                'supplier_name',
                'apifdf_type',
                'description',
                'unit_price',
                'quantity',
                'duty',
                'supplier_address',
                'importer_address',
                'importer_city_state',
                'import_date',
              ]}
            >
              <Column
                field="importer_name"
                header="Importer Name"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'importer_name')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="supplier_name"
                header="Supplier Name"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'supplier_name')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="apifdf_type"
                header="Type"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'apifdf_type')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="description"
                header="Description"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'description')}
                style={{ minWidth: '200px' }}
              />
              <Column
                field="unit_price"
                header="Unit Price"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => currencyBodyTemplate(rowData, 'unit_price')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="quantity"
                header="Quantity"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => quantityBodyTemplate(rowData, 'quantity')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="duty"
                header="Duty"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => currencyBodyTemplate(rowData, 'duty')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="supplier_address"
                header="Supplier Address"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'supplier_address')}
                style={{ minWidth: '180px' }}
              />
              <Column
                field="importer_address"
                header="Importer Address"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'importer_address')}
                style={{ minWidth: '180px' }}
              />
              <Column
                field="importer_city_state"
                header="City/State"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={importTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'importer_city_state')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="import_date"
                header="Import Date"
                filter
                filterPlaceholder="Date Range"
                showFilterMenu={false}
                showClearButton={true}
                filterElement={importDateFilterTemplate}
                body={rowData => dateBodyTemplate(rowData, 'import_date')}
                style={{ minWidth: '250px' }}
              />
            </DataTable>
          </div>
        </TabPanel>

        <TabPanel
          header={
            <div
              onClick={() => setActiveTabIndex(1)}
              className={`apifdf-tab-header ${activeTabIndex === 1 ? 'apifdf-tab-selected' : 'apifdf-tab-unselected'}`}
            >
              Export
            </div>
          }
        >
          <div className="apifdf-datatable-wrapper">
            {isExportLoading && <LoadingOverlay />}
            <DataTable
              value={exportData}
              lazy
              paginator
              first={exportPagination.first}
              rows={exportPagination.pageSize}
              totalRecords={exportPagination.totalRecords}
              onPage={onExportPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
              emptyMessage="No export records found"
              scrollable
              scrollHeight="100rem"
              stripedRows
              filters={exportFilters}
              onFilter={onExportFilterChange}
              filterDisplay="row"
              loading={isExportLoading}
              globalFilterFields={[
                'exporter_name',
                'exporter_address',
                'exporter_city_address',
                'exporter_country',
                'apifdf_type',
                'description',
                'unit_price',
                'quantity',
                'consignee_name',
                'export_date',
              ]}
            >
              <Column
                field="exporter_name"
                header="Exporter Name"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'exporter_name')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="exporter_address"
                header="Exporter Address"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'exporter_address')}
                style={{ minWidth: '180px' }}
              />
              <Column
                field="exporter_city_address"
                header="City Address"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'exporter_city_address')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="exporter_country"
                header="Country"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'exporter_country')}
                style={{ minWidth: '120px' }}
              />
              <Column
                field="apifdf_type"
                header="Type"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'apifdf_type')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="description"
                header="Description"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'description')}
                style={{ minWidth: '200px' }}
              />
              <Column
                field="unit_price"
                header="Unit Price"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => currencyBodyTemplate(rowData, 'unit_price')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="quantity"
                header="Quantity"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => quantityBodyTemplate(rowData, 'quantity')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="consignee_name"
                header="Consignee Name"
                filter
                filterPlaceholder="Search"
                showFilterMenu={false}
                showClearButton={true}
                filterMatchMode={FilterMatchMode.CONTAINS}
                filterElement={exportTextFilterTemplate}
                body={rowData => textBodyTemplate(rowData, 'consignee_name')}
                style={{ minWidth: '150px' }}
              />
              <Column
                field="export_date"
                header="Export Date"
                filter
                filterPlaceholder="Date Range"
                showFilterMenu={false}
                showClearButton={true}
                filterElement={exportDateFilterTemplate}
                body={rowData => dateBodyTemplate(rowData, 'export_date')}
                style={{ minWidth: '250px' }}
              />
            </DataTable>
          </div>
        </TabPanel>
      </TabView>

      {/* Note about APIFDF data search criteria */}
      <p className="apifdf-note">
        * APIFDF data is searched based on molecule and strength, you might see differences in the
        dosage form or full name.
      </p>
    </div>
  );
};

export default APIFDFSection;
